import styled from 'styled-components/macro';

import { useTranslation } from 'react-i18next';
import InfoPopover from '../Fields/InfoPopover';
import { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/solid';

const HeadingBar = styled.section<{ collapsible?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  height: 42px;
  border-bottom: 1px solid #d9d9d9;
  cursor: ${(props) => (props.collapsible ? 'pointer' : 'default')};
`;

const HeadingIconContainer = styled.label`
  height: 30px;
  width: 30px;
  background: #eaf5fa;
  margin: 0 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
`;

const DragIconContainer = styled.label`
  height: 30px;
  width: 30px;
  // background: #eaf5fa;
  margin: 0 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
`;

const HeadingIcon = styled.img`
  height: 17px;
  width: 17px;
`;

const HeadingLabel = styled.label`
  font-family: Open Sans, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #262626;
  margin: 0px 2px;
`;

const Body = styled.section<BodyStyles>`
  padding: ${({ padding }) => padding};
  display: ${({ isCollapsed, display }) => (isCollapsed ? 'none' : display)};
  flex-direction: ${({ flexDirection }) => flexDirection};
  flex: ${({ flex }) => flex};
  justify-content: ${({ justifyContent }) => justifyContent};
  transition: max-height 0.3s ease-out;
`;

const CollapseIcon = styled.span`
  margin-left: auto;
  margin-right: 10px;
  display: flex;
  align-items: center;
`;

type BodyStyles = {
  justifyContent?: string;
  flex?: number;
  display?: string;
  flexDirection?: string;
  padding?: string;
  isCollapsed?: boolean;
};

type Props = {
  title: string;
  icon: string | JSX.Element;
  alt: string;
  height?: string;
  children: any;
  width?: string;
  justifyContent?: string;
  flex?: number;
  display?: string;
  flexDirection?: string;
  desktopWidth?: string;
  className?: string;
  bodyStyle?: any;
  actions?: JSX.Element;
  headingClass?: string;
  dragIcon?: string | JSX.Element;
  popoverContent?: string | JSX.Element;
  popOverOnclick?: boolean;
  showModalHeader?: boolean;
  showCloseIcon?: boolean;
  showCardHeader?: boolean;
  collapsible?: boolean;
  defaultCollapse?: boolean;
};
const Card = (props: Props) => {
  const {
    alt,
    icon,
    title,
    children,
    justifyContent,
    flex,
    display,
    flexDirection,
    className,
    bodyStyle,
    actions,
    headingClass = '',
    dragIcon = null,
    popoverContent = '',
    popOverOnclick = false,
    showModalHeader = true,
    showCloseIcon = true,
    showCardHeader = true,
    collapsible = false,
    defaultCollapse = false
  } = props;

  const [isCollapsed, setIsCollapsed] = useState(defaultCollapse);

  const toggleCollapse = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <section
      data-testid={title}
      className={`mb-4 flex flex-col rounded-lg border border-solid border-white bg-white ${className}`}
    >
      {showCardHeader && (
        <HeadingBar className={headingClass} onClick={toggleCollapse} collapsible={collapsible}>
          {dragIcon && (
            <DragIconContainer>
              {typeof dragIcon === 'string' ? (
                <HeadingIcon src={dragIcon} alt={alt} />
              ) : (
                <div>{dragIcon}</div>
              )}
            </DragIconContainer>
          )}
          {icon && (
            <HeadingIconContainer>
              {typeof icon === 'string' ? <HeadingIcon src={icon} alt={alt} /> : <div>{icon}</div>}
            </HeadingIconContainer>
          )}
          <HeadingLabel>{title}</HeadingLabel>
          {popoverContent ? (
            <InfoPopover
              className="ml-2"
              popOverOnclick={popOverOnclick}
              content={popoverContent}
              showModalHeader={showModalHeader}
              showCloseIcon={showCloseIcon}
            />
          ) : null}
          {actions}
          {collapsible && (
            <CollapseIcon>
              {isCollapsed ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              )}
            </CollapseIcon>
          )}
        </HeadingBar>
      )}

      <Body
        justifyContent={justifyContent}
        flex={flex}
        display={display}
        flexDirection={flexDirection}
        style={bodyStyle}
        padding={showCardHeader ? '20px' : '0px'}
        isCollapsed={isCollapsed}
      >
        {children}
      </Body>
    </section>
  );
};

export default Card;
